import React, { useEffect } from 'react';

import { IFacialRecognitionProcess } from 'shared/domain/entities';
import { useEventTracker } from 'shared/presentation/hooks';

import * as S from './styles';

const CAF_ERROR_EVENTS = [
  'SDK_CAPTURE_FAILED',
  'LIVENESS_IPROOV_ERROR',
  'SDK_UPLOAD_INVALID',
  'SDK_CAPTURE_FAILED',
  'SDK_CAPTURE_INVALID',
] as const;
const CAF_DENIED_EVENTS = ['SDK_CAMERA_DENIED', 'SDK_CAPTURE_DENIED'] as const;
const CAF_INFO_EVENTS = [
  'ONBOARDING_STARTED',
  'GET_CAMERA_PERMISSIONS',
  'LIVENESS_CAPTURE',
] as const;
const CAF_FINISH_EVENTS = ['FINISH_FACE_AUTH', 'ONBOARDING_FINISHED'] as const;

interface ICafEventData {
  code:
    | (typeof CAF_FINISH_EVENTS)[number]
    | (typeof CAF_INFO_EVENTS)[number]
    | (typeof CAF_ERROR_EVENTS)[number]
    | (typeof CAF_DENIED_EVENTS)[number];
}

function isCafEventData(data: unknown): data is ICafEventData {
  return !!data && typeof data === 'object' && 'code' in data;
}

interface IOnboardingLinkProps {
  process: IFacialRecognitionProcess;
  link: string;
  onOnboardingFinished(): void;
}

const OnboardingLink: React.FC<IOnboardingLinkProps> = ({
  process,
  link,
  onOnboardingFinished,
}) => {
  const eventTracker = useEventTracker();

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (!isCafEventData(event.data)) return;

      if (CAF_FINISH_EVENTS.includes(event.data.code as any)) {
        onOnboardingFinished();
      }

      if (CAF_INFO_EVENTS.includes(event.data.code as any)) {
        eventTracker.register('ONBOARDING_INFO', {
          data: event.data,
          process: process.trackingPayload,
        });
      }

      if (CAF_DENIED_EVENTS.includes(event.data.code as any)) {
        eventTracker.register('ONBOARDING_PERMISSION_DENIED', {
          data: event.data,
          process: process.trackingPayload,
        });
      }

      if (CAF_ERROR_EVENTS.includes(event.data.code as any)) {
        eventTracker.register('ONBOARDING_ERROR', {
          data: event.data,
          process: process.trackingPayload,
        });
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [eventTracker, onOnboardingFinished, process]);

  return (
    <S.Container
      title="Caf"
      src={link}
      allow="geolocation;camera;fullscreen;accelerometer;gyroscope;magnetometer;"
      allowFullScreen
    />
  );
};

export default OnboardingLink;
